import type { SRT_ColumnDef } from '@/shared/components/master-table'
import type { ScenarioRunSetRow } from '../model/types'

import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { formatDistanceToNow, intlFormat } from 'date-fns'
import { ArrowUpRightIcon, BoxesIcon, BoxIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { UsernameById } from '@/entities/user'

import { createSRTColumnHelper } from '@/shared/components/master-table'
import { LinkButton } from '@/shared/components/ui/link-button'
import { RelativeTimeCard } from '@/shared/components/ui/relative-time-card'
import { Separator } from '@/shared/components/ui/separator'
import { getLangCode, useLocale } from '@/shared/i18n'

const columnsHelper = createSRTColumnHelper<ScenarioRunSetRow>()
export const useWhatifRunsTableColumns = () => {
	const { t } = useTranslation()
	const currentLocale = useLocale()

	return useMemo(
		() =>
			[
				columnsHelper.accessor('id', {
					header: t('general_name'),
					Cell: ({ cellValue, row }) => {
						const { t } = useTranslation()

						return (
							<div className='flex items-center gap-4 truncate'>
								{row.original.combinations.length > 1 ? (
									<div className='flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 text-primary'>
										<BoxesIcon className='size-4' />
									</div>
								) : (
									<div className='flex size-8 shrink-0 items-center justify-center rounded-lg border border-purple-500/30 bg-purple-500/10 text-purple-500'>
										<BoxIcon className='size-4' />
									</div>
								)}

								<div className='flex flex-col justify-center gap-1 truncate'>
									<RelativeTimeCard
										asChild
										date={new Date(row.original.created_at + 'Z')}
										locale={getLangCode(currentLocale)}
									>
										<LinkButton
											className='group flex items-center gap-1 font-semibold'
											asChild
										>
											<Link
												to='/p/whatif/runs/$scenarioRunSetId/summary'
												params={{ scenarioRunSetId: cellValue }}
											>
												{intlFormat(new Date(row.original.created_at + 'Z'), {
													year: 'numeric',
													month: 'numeric',
													day: 'numeric',
													hour: 'numeric',
													minute: 'numeric',
													second: 'numeric',
												})}
												<ArrowUpRightIcon className='size-4 opacity-0 group-hover:opacity-100' />
											</Link>
										</LinkButton>
									</RelativeTimeCard>
									<div className='flex items-center gap-2 truncate text-sm text-muted-foreground'>
										<span className='flex items-center gap-1'>
											{t('combinations', { defaultValue: 'Combinations' })}:{' '}
											<span className='text-foreground'>
												{row.original.combinations?.length}
											</span>
										</span>
										<Separator orientation='vertical' className='h-3!' />
										<div className='flex items-center gap-1'>
											<span>
												{t('optimization_groups', {
													defaultValue: 'Optimization groups',
												})}
												:
											</span>
											<span className='text-foreground'>
												{row.original.optimization_groups?.length}
											</span>
										</div>
										<Separator orientation='vertical' className='h-3!' />
										<span className='flex items-center gap-1'>
											{t('scenarios', { defaultValue: 'Scenarios' })}:{' '}
											<span className='text-foreground'>
												{row.original.scenarios?.length}
											</span>
										</span>
									</div>
								</div>
							</div>
						)
					},
				}),
				columnsHelper.display({
					id: 'metadata',
					header: t('whatif.runs.column.metadata', {
						defaultValue: 'Metadata',
					}),
					Cell: ({ row }) => (
						<div>
							<UsernameById userId={row.original.created_by} />
							<div className='flex items-center gap-1 text-sm text-muted-foreground'>
								{formatDistanceToNow(new Date(row.original.created_at + 'Z'), {
									addSuffix: true,
								})}
							</div>
						</div>
					),
					size: 200,
				}),
			] as SRT_ColumnDef<ScenarioRunSetRow>[],
		[]
	)
}
